// Breach Step 8: Remediation
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { CalendarInput } from '../../../../components/ui/calendar-input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/select';
import { FieldError } from '../../../../components/ui/field-error';
import { Plus, Trash2, RefreshCw } from 'lucide-react';
import { cn } from '../../../../lib/utils';

export default function Step8Remediation({ formData, updateField, errors = {}, onValidationChange }) {
  const addRemediationAction = () => {
    const newAction = {
      id: `action-${Date.now()}`, action: '', owner: '', timeframe: 'short_term', deadline: '', status: 'not_started'
    };
    updateField('remediation_actions', [...(formData.remediation_actions || []), newAction]);
  };

  const updateRemediationAction = (actionId, field, value) => {
    const updated = (formData.remediation_actions || []).map(a =>
      a.id === actionId ? { ...a, [field]: value } : a
    );
    updateField('remediation_actions', updated);

    // Clear error for this field if it now has a value
    if (onValidationChange && value && (typeof value === 'string' ? value.trim() : value)) {
      const newErrors = { ...errors };
      const idx = (formData.remediation_actions || []).findIndex(a => a.id === actionId);
      if (idx >= 0) {
        if (field === 'action') delete newErrors[`remediation_action_${idx}_action`];
        if (field === 'owner') delete newErrors[`remediation_action_${idx}_owner`];
        if (field === 'deadline') delete newErrors[`remediation_action_${idx}_deadline`];
      }
      onValidationChange(newErrors);
    }
  };

  const removeRemediationAction = (actionId) => {
    const updated = (formData.remediation_actions || []).filter(a => a.id !== actionId);
    updateField('remediation_actions', updated);
  };

  const actions = formData.remediation_actions || [];

  return (
    <div className="space-y-6">
      <div><h2 className="text-2xl font-bold">Remediation</h2><p className="text-muted-foreground mt-1">Document measures to prevent recurrence.</p></div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Remediation Actions</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addRemediationAction} data-testid="add-action-btn">
              <Plus className="h-4 w-4 mr-1" />Add Action
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {actions.length === 0 ? (
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <RefreshCw className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground font-medium">No remediation actions added yet</p>
              <p className="text-sm text-muted-foreground mt-1">Click "Add Action" to add remediation steps</p>
            </div>
          ) : (
            <div className="space-y-4">
              {actions.map((action, index) => (
                <div key={action.id} className="border rounded-lg p-4 space-y-4" data-testid={`action-card-${index}`}>
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm">Action {index + 1}</h4>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeRemediationAction(action.id)}
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`remove-action-${index}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div>
                    <Label>Remediation Action <span className="text-destructive">*</span></Label>
                    <Textarea value={action.action || ''} onChange={(e) => updateRemediationAction(action.id, 'action', e.target.value)}
                      placeholder="Describe the remediation action to be taken..." className={cn("mt-1.5", errors[`remediation_action_${index}_action`] && "border-destructive bg-destructive/5")} rows={3} data-testid={`action-text-${index}`} />
                    {errors[`remediation_action_${index}_action`] && <FieldError message={errors[`remediation_action_${index}_action`]} />}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <Label>Owner <span className="text-destructive">*</span></Label>
                      <Input value={action.owner || ''} onChange={(e) => updateRemediationAction(action.id, 'owner', e.target.value)}
                        placeholder="Person responsible" className={cn("mt-1.5", errors[`remediation_action_${index}_owner`] && "border-destructive bg-destructive/5")} data-testid={`action-owner-${index}`} />
                      {errors[`remediation_action_${index}_owner`] && <FieldError message={errors[`remediation_action_${index}_owner`]} />}
                    </div>
                    <div>
                      <Label>Timeframe <span className="text-destructive">*</span></Label>
                      <Select value={action.timeframe || 'short_term'} onValueChange={(v) => updateRemediationAction(action.id, 'timeframe', v)}>
                        <SelectTrigger className="mt-1.5" data-testid={`action-timeframe-${index}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">Immediate</SelectItem>
                          <SelectItem value="short_term">Short Term</SelectItem>
                          <SelectItem value="medium_term">Medium Term</SelectItem>
                          <SelectItem value="long_term">Long Term</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Deadline <span className="text-destructive">*</span></Label>
                      <div className={cn("mt-1.5", errors[`remediation_action_${index}_deadline`] && "border-destructive bg-destructive/5 rounded")}>
                        <CalendarInput data-testid={`action-deadline-${index}`} value={action.deadline || ''}
                          onChange={(date) => updateRemediationAction(action.id, 'deadline', date)} placeholder="DD/MM/YYYY"
                          className={errors[`remediation_action_${index}_deadline`] && "border-destructive bg-destructive/5"} />
                      </div>
                      {errors[`remediation_action_${index}_deadline`] && <FieldError message={errors[`remediation_action_${index}_deadline`]} />}
                    </div>
                    <div>
                      <Label>Status <span className="text-destructive">*</span></Label>
                      <Select value={action.status || 'not_started'} onValueChange={(v) => updateRemediationAction(action.id, 'status', v)}>
                        <SelectTrigger className="mt-1.5" data-testid={`action-status-${index}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not_started">Not Started</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="complete">Complete</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
